import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import { getFreeClassSchedulesAPI } from "../../../services/portal/student/studentService";
import { scheduleDate, scheduleTime } from "../../../utility/Common";
import { CalendarIcon } from "../../../icons/CalendarIcon";
import { TimeIcon } from "../../../icons/TimeIcon";
import { RoutePaths } from "../../../utility/RoutePaths";

const FreeClassesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    loadFreeClasses();
  }, []);

  const loadFreeClasses = async () => {
    setLoading(true);
    try {
      const res = await getFreeClassSchedulesAPI();
      if (res && res.IsSuccessful && res.Data) {
        const list = [];
        res.Data.forEach((classItem) => {
          list.push(classItem);
        });
        setSchedules(list);
      }
    } catch (error) {
      console.error("Error loading free classes:", error);
      showErrorToastMsg("Failed to load free classes");
    } finally {
      setLoading(false);
    }
  };

  const openScheduleDetails = (scheduleItem) => {
    navigate(
      `${RoutePaths.STUDENT_CLASS_SCHEDULE_DETAILS_PAGE}?classId=${scheduleItem.ClassId}&scheduleId=${scheduleItem.ClassScheduleId}`,
    );
  };

  const renderCard = (item) => {
    const imgSrc = item.Image
      ? item.Image.startsWith("data:")
        ? item.Image
        : `data:image/jpeg;base64,${item.Image}`
      : "/images/no_image.jpg";

    return (
      <div
        key={item.ClassScheduleId}
        onClick={(e) => {
          if (e.target.closest("button")) return;
          openScheduleDetails(item);
        }}
        className="group bg-white rounded-xl overflow-hidden shadow-md transition-all duration-300 cursor-pointer hover:shadow-xl hover:-translate-y-1"
      >
        {/* Image header */}
        <div className="relative h-48 overflow-hidden bg-gray-100">
          <img
            src={imgSrc}
            alt={item.ClassName}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white">
              Free
            </span>
          </div>
          <div className="absolute bottom-2 left-3 right-3">
            <h3 className="text-lg font-bold text-white drop-shadow-sm line-clamp-1">
              {item.ClassName}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 h-32">
          {item.Description && (
            <p className="text-xl font-bold text-gray-900 line-clamp-2">
              {item.Description}
            </p>
          )}
          <div className="flex gap-2 text-gray-700">
            <CalendarIcon />
            <span className="text-sm font-medium">
              {scheduleDate(item.StartDate, item.EndDate)}
            </span>
          </div>
          <div className="flex gap-2 text-gray-700">
            <TimeIcon />
            <span className="text-sm font-medium">
              {scheduleTime(item.StartDate, item.EndDate)}
            </span>
          </div>
        </div>

        <div className="p-4">
          <div className="flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openScheduleDetails(item);
              }}
              className="w-auto px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors bg-primary-600 text-white hover:bg-primary-700"
            >
              Join
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      title="Free Classes"
      subtitle="Browse available free class sessions"
    >
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-16">
          <svg
            className="mx-auto w-16 h-16 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500 text-lg font-medium">
            No free classes available
          </p>
          <p className="text-gray-400 text-sm mt-1">
            Check back later for new sessions.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-6">
          {schedules.map((item) => renderCard(item))}
        </div>
      )}
    </DashboardLayout>
  );
};

export default FreeClassesPage;
